// 1 section on Resources and files (AdminResources.dc.html): its name and count, move up and down,
// Rename, Delete and Add item; each item with its kind, title, badges, line and opens, move up and
// down, Edit and Remove (with its confirm); the empty line; and what was removed from it in the last
// 30 days, with Put it back (the stage 5 map's restore, which the board doesn't draw).
import { RsIcon } from './Icons.jsx'
import { dayMonth } from '../../lib/format.js'
import { plural } from '../ui/format.js'

function IconButton({ label, dim, onClick, size = 'md', disabled }) {
  return (
    <button type="button" className={`ar-icon-btn ar-icon-btn--${size}`} aria-label={label} onClick={onClick}
      aria-disabled={dim || undefined} disabled={disabled}>
      <RsIcon name={label.startsWith('Move') && label.endsWith(' up') ? 'up' : 'down'} size={size === 'md' ? 15 : 14} bold />
    </button>
  )
}

function ItemRow({ row, index, count, editing, confirming, busy, onMove, onEdit, onAskRemove, onRemove, onKeep }) {
  return (
    <li className={`ar-item${editing ? ' ar-item--editing' : ''}`}>
      <div className="ar-item-row">
        <span className="ar-kind" aria-hidden="true"><RsIcon name={row.kind} /></span>
        <span className="ar-item-text">
          <span className="ar-item-title-line">
            <span className="ar-item-title">{row.title}</span>
            {row.isNew && <span className="ar-badge ar-badge--new">NEW</span>}
            {row.updated && <span className="ar-badge">{row.updated}</span>}
          </span>
          <span className="ar-item-meta">{row.meta}</span>
          {row.opens && <span className={`ar-item-opens${row.opens.warn ? ' ar-item-opens--warn' : ''}`}>{row.opens.text}</span>}
        </span>
        <span className="ar-item-actions">
          <IconButton size="sm" label={`Move ${row.title} up`} dim={index === 0} disabled={busy} onClick={() => onMove(-1)} />
          <IconButton size="sm" label={`Move ${row.title} down`} dim={index === count - 1} disabled={busy} onClick={() => onMove(1)} />
          <button type="button" className="ar-btn ar-btn--line ar-btn--row" onClick={onEdit} disabled={busy}>Edit</button>
          <button type="button" className="ar-btn ar-btn--bare ar-btn--row" onClick={onAskRemove} disabled={busy} aria-expanded={confirming}>Remove</button>
        </span>
      </div>
      {confirming && (
        <div className="ar-confirm" role="group" aria-label={`Remove ${row.title}`}>
          <span className="ar-confirm-text">Remove {row.title}? Members lose it straight away. The file stays in storage for 30 days, in case you want it back.</span>
          <span className="ar-confirm-actions">
            <button type="button" className="ar-btn ar-btn--ink ar-btn--confirm" onClick={onRemove} disabled={busy} aria-busy={busy || undefined}>Remove</button>
            <button type="button" className="ar-btn ar-btn--line ar-btn--confirm" onClick={onKeep} disabled={busy}>Keep it</button>
          </span>
        </div>
      )}
    </li>
  )
}

export function SectionCard({
  section, index, count, rows, removed, renaming, renameValue, message, confirmDelete, editingId, confirmId, busy,
  onMove, onRename, onRenameValue, onSaveRename, onCancelRename, onDelete, onConfirmDelete, onKeepSection, onAdd,
  onMoveItem, onEdit, onAskRemove, onRemove, onKeep, onPutBack,
}) {
  const headId = `ar-sec-${section.id}`
  return (
    <section className="ar-section" aria-labelledby={headId}>
      <div className="ar-section-head">
        {renaming ? (
          <form className="ar-rename" onSubmit={e => { e.preventDefault(); onSaveRename() }}>
            <h2 className="ar-visually-hidden" id={headId}>{section.name}</h2>
            <input className="ar-input ar-input--rename" aria-label="Section name" value={renameValue} maxLength={60}
              onChange={e => onRenameValue(e.target.value)} autoFocus />
            <button type="submit" className="ar-btn ar-btn--ink ar-btn--head" disabled={busy}>Save</button>
            <button type="button" className="ar-btn ar-btn--line ar-btn--head" onClick={onCancelRename} disabled={busy}>Cancel</button>
          </form>
        ) : (
          <>
            <span className="ar-section-name-line">
              <h2 className="ar-section-name" id={headId}>{section.name}</h2>
              <span className="ar-section-count">{plural(rows.length, 'item')}</span>
            </span>
            <span className="ar-section-actions">
              <IconButton label={`Move ${section.name} up`} dim={index === 0} disabled={busy} onClick={() => onMove(-1)} />
              <IconButton label={`Move ${section.name} down`} dim={index === count - 1} disabled={busy} onClick={() => onMove(1)} />
              <button type="button" className="ar-btn ar-btn--bare ar-btn--head" onClick={onRename} disabled={busy}>Rename</button>
              <button type="button" className="ar-btn ar-btn--bare ar-btn--head" onClick={onDelete} disabled={busy}>Delete</button>
              <button type="button" className="ar-btn ar-btn--ink ar-btn--head ar-btn--add" onClick={onAdd} disabled={busy}>Add item</button>
            </span>
          </>
        )}
      </div>
      {message && <div className="ar-section-msg" role="status">{message}</div>}
      {confirmDelete && (
        <div className="ar-confirm ar-confirm--section" role="group" aria-label={`Delete ${section.name}`}>
          <span className="ar-confirm-text">
            Delete {section.name}? What was removed from it in the last 30 days goes too, and can't be put back.
          </span>
          <span className="ar-confirm-actions">
            <button type="button" className="ar-btn ar-btn--ink ar-btn--confirm" onClick={onConfirmDelete} disabled={busy}>Delete section</button>
            <button type="button" className="ar-btn ar-btn--line ar-btn--confirm" onClick={onKeepSection} disabled={busy}>Keep it</button>
          </span>
        </div>
      )}
      {rows.length > 0 && (
        <ul className="ar-items">
          {rows.map((row, i) => (
            <ItemRow key={row.id} row={row} index={i} count={rows.length} busy={busy}
              editing={editingId === row.id} confirming={confirmId === row.id}
              onMove={dir => onMoveItem(i, dir)} onEdit={() => onEdit(row.id)} onAskRemove={() => onAskRemove(row.id)}
              onRemove={() => onRemove(row.id)} onKeep={onKeep} />
          ))}
        </ul>
      )}
      {rows.length === 0 && <p className="ar-section-empty">Nothing in this section yet. Members don't see an empty section.</p>}
      {removed.length > 0 && (
        <div className="ar-removed">
          <h3 className="ar-removed-head">Removed in the last 30 days</h3>
          <ul className="ar-removed-list">
            {removed.map(r => (
              <li key={r.id} className="ar-removed-row">
                <span className="ar-removed-text"><span className="ar-removed-title">{r.title}</span> Removed {dayMonth(r.removed_at)}.</span>
                <button type="button" className="ar-btn ar-btn--line ar-btn--row" onClick={() => onPutBack(r.id)} disabled={busy}>Put it back</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
